import React, { useState, useEffect } from 'react';
import axios from 'axios';
import YouTubeEmbed from '../../components/YouTubeEmbed';
import '../../styles/SetDev.css';
import backendUrl from '../../BackendUrl';

const SetDev = () => {
  // State for the list of dev logs
  const [devs, setDevs] = useState([]);

  // State to control whether the add/edit modal (canvas) is visible
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);
  // State to decide if we’re editing (true) or adding (false)
  const [isEditMode, setIsEditMode] = useState(false);
  // The dev selected for editing (or null when adding)
  const [selectedDev, setSelectedDev] = useState(null);
  const adminToken = localStorage.getItem('adminToken');

  // Form data for dev information
  const [formData, setFormData] = useState({
    log: '',
    url: '',
    description: '',
    date: '',
    ready: false,
  });

  // Fetch the dev logs when the component mounts
  useEffect(() => {
    fetchDevs();
  }, []);

  const fetchDevs = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/devs/get-dev`, {
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
      });
      setDevs(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching dev logs:', error);
    }
  };

  // Opens the modal for adding a new dev
  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedDev(null);
    setFormData({
      log: '',
      url: '',
      description: '',
      date: '',
    });
    setIsCanvasVisible(true);
  };

  // Opens the modal for editing an existing dev log and pre-fills the form
  const handleEditClick = (dev) => {
    setIsEditMode(true);
    setSelectedDev(dev);
    setFormData({
      log: dev.log,
      url: dev.url,
      description: dev.description,
      // Format the date for the input (assumes ISO string)
      date: dev.date ? dev.date.split('T')[0] : '',
      ready: !!dev.ready,
    });
    setIsCanvasVisible(true);
  };

  // Closes the modal
  const handleCancel = () => {
    setIsCanvasVisible(false);
  };

  // Handle changes in the form inputs
  const handleFormChange = (e) => {
    const { name, type, value, checked } = e.target;
    const next = type === 'checkbox' ? checked : value;
    setFormData({ ...formData, [name]: next });
  };

  // Handle form submission for both adding and editing
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        log: formData.log,
        url: formData.url,
        description: formData.description,
        date: formData.date,
        ready: !!formData.ready,
      };

      if (isEditMode && selectedDev) {
        // Update an existing dev log
        await axios.put(`${backendUrl}/api/devs/update-dev/${selectedDev._id}`, payload, { headers: { Authorization: `Bearer ${adminToken}` } });
      } else {
        // Create a new dev
        await axios.post(`${backendUrl}/api/devs/add-dev`, payload, { headers: { Authorization: `Bearer ${adminToken}` } });
      }
      // Refresh the list and close the modal
      fetchDevs();
      setIsCanvasVisible(false);
    } catch (error) {
      console.error('Error saving dev:', error);
    }
  };

  // Delete a dev log
  const handleDelete = async (devId) => {
    if (window.confirm('Are you sure you want to delete this dev?')) {
      try {
        await axios.delete(`${backendUrl}/api/devs/delete-dev/${devId}`, {headers: {Authorization: `Bearer ${adminToken}`,},});
        fetchDevs();
      } catch (error) {
        console.error('Error deleting dev:', error);
      }
    }
  };

  return (
    <div className="admin-devs-page">
      <h1>Manage Devs</h1>
      <button onClick={handleAddClick} className="add-dev-btn">Add Dev Log</button>

      <div className="devs-list">
        {devs.map((dev) => (
          <div key={dev._id} className="dev-card">
            <div className="dev-details">
              <h3>
                {dev.date ? new Date(dev.date).toLocaleDateString() : 'N/A'} - {dev.log}
                {dev.ready && <span className="ready-badge">Ready</span>}
              </h3>
              <p>{dev.description}</p>
              <p>Video: </p>
              <YouTubeEmbed url={dev.url} />
            </div>
            <div className="dev-actions">
              <button onClick={() => handleEditClick(dev)}>Edit</button>
              <button onClick={() => handleDelete(dev._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal canvas for add/edit form */}
      {isCanvasVisible && (
        <div className="dev-canvas-overlay">
          <div className="dev-canvas">
            <button className="close-button" onClick={handleCancel}>X</button>
            <h2>{isEditMode ? 'Edit Dev' : 'Add Dev'}</h2>
            <form onSubmit={handleFormSubmit} className="dev-form">
              <div className="form-group">
                <label>Dev Log:</label>
                <input 
                  type="text" 
                  name="log" 
                  value={formData.log} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Url:</label>
                <input
                  type="text" 
                  name="url" 
                  value={formData.url} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description" 
                  value={formData.description} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Date:</label>
                <input 
                  type="date" 
                  name="date" 
                  value={formData.date} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="ready"
                    checked={!!formData.ready}
                    onChange={handleFormChange}
                  />
                  Ready
                </label>
              </div>
              <div className="form-buttons">
                <button type="submit">{isEditMode ? 'Update Dev' : 'Add Dev'}</button>
                <button type="button" onClick={handleCancel}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetDev;